import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, concat, throwError, Subject } from 'rxjs';
import { catchError, tap, map, shareReplay, takeUntil } from 'rxjs/operators';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { CacheService } from '@app/services/cache.service';
import { User } from '@microsoft/microsoft-graph-types';
import { Person } from '@microsoft/microsoft-graph-types';
import { User as UserModel } from '@app/models/user';

import { GraphBatchData, GraphBatchRequest } from '@app/models/graph-batch-request';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient, private cache: CacheService, private snackBar: MatSnackBar) { }

  private readonly cacheKeyPrefix = 'UserService';

  public useCache = true;

  private getMyDetailsFromMsGraph$: Observable<User>;
  private getUserPhotoByUpn$ = new Map<string, Observable<string>>();
  private getOrCreateMe$: Observable<UserModel>;
  private reload$ = new Subject<void>();

  private getSingleUserByUpn$ = new Map<string, Observable<User>>();
  private defaultSelectProperties = 'id,userPrincipalName,displayName,mail';

  public reload() {
    this.reload$.next();
    this.getOrCreateMe$ = null;
    if (this.useCache) {
      const cacheKey = `${this.cacheKeyPrefix}-getOrCreateMe`;
      this.cache.removeFromPersistentCache(cacheKey);
    }
  }

  getOrCreateMe(): Observable<UserModel> {
    if (this.getOrCreateMe$) {
      return this.getOrCreateMe$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getOrCreateMe`;
      this.getOrCreateMe$ = this.http.get<UserModel>(`${Constants.API_ENDPOINT}/users/me`, HttpHelper.defaultRequestOptions)
        .pipe(map(user => {
          if (user) {
            console.log('Fetched user from DB', user);
            this.useCache && this.cache.set(cacheKey, user);
            return user;
          }
        }),
          catchError(err => {
            if (err.status === 404) {
              return this.http.post<UserModel>(`${Constants.API_ENDPOINT}/users`, new UserModel(), HttpHelper.defaultRequestOptions)
                .pipe(map(user => {
                  this.useCache && this.cache.set(cacheKey, user);
                  return user;
                }));
            } else {
              return throwError(err);
            }
          }),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<UserModel>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getOrCreateMe$);
        }
      }
      return this.getOrCreateMe$;
    }
  }

  // Returns users based on their UPN. If UPN is not present in DB the user is created.
  ensureUsersByUpn(userUpns: string[]): Observable<Array<UserModel>> {

    const cacheKey = `${this.cacheKeyPrefix}-getUsersByUpn-${userUpns.join('-')}`;

    let params = new HttpParams();
    params = params.append('userUpns', userUpns.map(u => encodeURIComponent(u)).join(','));

    const request$ = this.http.get<Array<UserModel>>(`${Constants.API_ENDPOINT}/Users/EnsureByUpn`, {
      ...HttpHelper.defaultRequestOptions,
      params: params
    })
      .pipe(map(users => {
        if (users) {
          console.log('Fetched or Created users by UPN:', users);
          this.useCache && this.cache.set(cacheKey, users);
          return users;
        }
      }));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<UserModel>>(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }
    return request$;
  }


  updateMe(user: UserModel): Observable<boolean> {
    const fetchOperation = this.http.put<UserModel>(`${Constants.API_ENDPOINT}/users/`, user, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          // invalidate cache
          this.reload();
          return true;
        }),
        catchError(HttpHelper.handleError('updateMe', false)));

    return fetchOperation;
  }


  getUserPhotoByUpn(userPrincipalName: string): Observable<string> {
    if (this.getUserPhotoByUpn$.has(userPrincipalName)) {
      return this.getUserPhotoByUpn$.get(userPrincipalName);
    } else {
      const apiEndPoint = `${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION}/users/${userPrincipalName}/photo/$value`;

      this.getUserPhotoByUpn$.set(userPrincipalName, this.http.get(apiEndPoint, {
        responseType: 'blob',
      })
        .pipe(map(result => {
          return URL.createObjectURL(result);
        }),
          catchError(err => {
            if (err.status === 404) {
              return of(null);
            } else {
              return throwError(err);
            }
          }),
          shareReplay({ bufferSize: 1, refCount: true })));
      return this.getUserPhotoByUpn$.get(userPrincipalName);
    }
  }

  /*
 * Queries MS Graph for multiple user photos.
 */
  getBatchUserPhotoByUpn(userPrincipalNames: Array<string>): Observable<Array<string>> {

    if (userPrincipalNames.length === 0) {
      return of(userPrincipalNames);
    }

    const data = new GraphBatchData();
    data.requests = userPrincipalNames.map((upn, index) => {
      return new GraphBatchRequest({
        id: index.toString(),
        method: 'GET',
        url: `/users/${upn}/photo/$value`,
        headers: {
          'responseType': 'blob'
        },
      });
    });

    const fetchOperation = this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/$batch`, data)
      .pipe(
        map(response => {
          return response.responses.map(r => {
            const upn = userPrincipalNames[r.id];

            if (r.status.toString().startsWith('4')) {
              return upn;
            }

            return `data:image/png;base64,${r.body}`;
          });
        }),
        catchError(HttpHelper.handleError('getBatchUserPhotoByUpn', {} as Array<string>)));

    return fetchOperation;
  }

  /*
   * Queries MS Graph for details about the signed in user.
   * Only specified properties are returned. Add more if you
   * need, see: https://docs.microsoft.com/en-us/graph/api/resources/user?view=graph-rest-1.0
   */
  getMyDetailsFromMsGraph(noCache?: boolean): Observable<User> {
    if (this.getMyDetailsFromMsGraph$) {
      return this.getMyDetailsFromMsGraph$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-myDetails`;
      const properties = [
        'id',
        'displayName',
        // 'assignedPlans',
        // 'city',
        // 'country',
        // 'jobTitle',
        // 'state',
        // 'department',
        // 'companyName',
        // 'officeLocation',
        'userPrincipalName',
      ].join(',');

      this.getMyDetailsFromMsGraph$ = this.http
        .get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/me/?$select=${properties}`)
        .pipe(
          map(response => {
            const result = response as User;
            console.log(`Fetched current user details from Graph: `, result);
            return result;
          }),
          tap((data) => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(HttpHelper.handleError('getMyDetailsFromMsGraph', {} as User, true)),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      if (this.useCache && !noCache) {
        const cachedData = this.cache.get<User>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getMyDetailsFromMsGraph$);
        }
      }

      return this.getMyDetailsFromMsGraph$;
    }
  }

  /*
  * Search for users using MS Graph
  */
  searchMsGraphForUsers(keyword: string, max: number, excludeExternal: boolean): Observable<Array<User>> {

    // Note: MS Graph does not have free form search on
    // users objects, so until this is available we use
    // multiple startsWith fitlers

    keyword = keyword.replace('#', '%23');  // replace # with entity

    const cacheKey = `${this.cacheKeyPrefix}-searchForUsers-${keyword}-${max}`;
    const cachedData = this.cache.get<Array<User>>(cacheKey);
    if (cachedData) {
      return of(cachedData);
    }

    let filter = `$filter=(startswith(displayName,'${keyword}') or startswith(givenName,'${keyword}') or startswith(surname,'${keyword}') or startswith(mail,'${keyword}') or startswith(userPrincipalName,'${keyword}'))`;

    if (excludeExternal) {
      filter += ` and userType eq 'Member'`;
    }

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/users?${filter}&$top=${max}`)
      .pipe(
        map(response => {
          const result = response.value as Array<User>;
          // console.log(`fetched users with keyword "${keyword}"`);
          // console.log(result);
          this.useCache && this.cache.set(cacheKey, result);
          return result;
        })
      );
    return fetchOperation;
  }

  ///
  /*
  * Fetch MS Graph users
  */
  getMsGraphUsers(userPrincipalNames: string[]): Observable<Array<User>> {
    const cacheKey = `${this.cacheKeyPrefix}-getMsGraphUsers-${userPrincipalNames.join('|')}`;
    const cachedData = this.cache.get<Array<User>>(cacheKey);
    if (cachedData) {
      return of(cachedData);
    }

    let filter = `$filter=userPrincipalName in ('`;
    filter += userPrincipalNames.join(`','`);
    filter += `')`;

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/users?${filter}`)
      .pipe(
        map(response => {
          const result = response.value as Array<User>;
           console.log(result);
          this.useCache && this.cache.set(cacheKey, result);
          return result;
        })
      );
    return fetchOperation;
  }

  getSingleUserByUpn(userPrincipalName: string): Observable<Person> {
    if (this.getSingleUserByUpn$.has(userPrincipalName)) {
      return this.getSingleUserByUpn$.get(userPrincipalName);
    } else {
      const httpParams = new HttpParams()
        .append('$select', this.defaultSelectProperties);

      const apiEndPoint = `${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION}/users/${userPrincipalName}`;

      this.getSingleUserByUpn$.set(userPrincipalName, this.http.get<any>(apiEndPoint, { params: httpParams })
        .pipe(
          map(response => {
            return response as Array<Person>;
          }),
          catchError(err => {
            if (err.status === 404) {
              return of(null);
            } else {
              return throwError(err);
            }
          }),
          shareReplay({ bufferSize: 1, refCount: true })));

      return this.getSingleUserByUpn$.get(userPrincipalName);
    }
  }

  /*
  * Search for related people using MS Graph
  */
  getRelatedPeople(top: number): Observable<Array<Person>> {

    const filter = 'personType/class eq \'Person\' and personType/subclass eq \'OrganizationUser\'';
    const cacheKey = `${this.cacheKeyPrefix}-getRelatedPeople-${top}`;
    const httpParams = new HttpParams()
      .append('$top', top.toString())
      .append('$filter', filter);

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/me/people`,
      { params: httpParams })
      .pipe(
        map(response => {
          return response.value as Array<Person>;
        }),
        tap(data => {
          this.useCache && this.cache.set(cacheKey, data);
        }),
        catchError(HttpHelper.handleError('getRelatedPeople', new Array<Person>(), true)));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<User>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }


}
